/**
 * Tema tercihi — açık, koyu ya da işletim sisteminin dediği.
 *
 * Palet `styles/index.css`'te hazırdı ve ölçülmüştü (`docs/design/theme-contrast.md`), ama
 * `.dark` sınıfını hiçbir şey eklemiyordu: koyu tema kullanıcıya **ulaşamıyordu**. Bu dosya o
 * eksik parçadır.
 *
 * **Tercih tarayıcıda saklanıyor, hesapta değil** (KARAR 2026-08-16, kullanıcı). Tema ekrana ait
 * bir özellik: aynı kişi ofisteki parlak monitörde açık, gece dizüstünde koyu ister. Hesapta
 * saklamak ikisine tek cevap dayatır, üstelik versiyonlu API sözleşmesine kalıcı bir alan ekler.
 */

export const THEME_STORAGE_KEY = 'mersisportal.theme'
export const DARK_CLASS = 'dark'

export const themes = ['light', 'dark', 'system'] as const

export type Theme = (typeof themes)[number]

/** Kullanıcının seçtiği tema değil, ekranda gerçekten çizilen tema. */
export type ResolvedTheme = 'light' | 'dark'

function isTheme(value: unknown): value is Theme {
  return themes.includes(value as Theme)
}

/**
 * Varsayılan **`system`**: hiçbir şey seçmemiş bir kullanıcının işletim sistemi koyu moddaysa
 * uygulamayı açık açmak, seçim değil kazadır.
 */
export function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)

    return isTheme(stored) ? stored : 'system'
  } catch {
    // Depolama kapalı olabilir (gizli sekme, kurumsal politika). Tema okunamadı diye uygulama
    // açılmamalı; sistem tercihi zaten makul bir cevap.
    return 'system'
  }
}

export function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    return prefersDark() ? 'dark' : 'light'
  }

  return theme
}

/**
 * Sınıfı köke yazar.
 *
 * Kök eleman (`<html>`), gövde değil: `color-scheme` buradan miras alınıyor ve tarayıcının kendi
 * çizdiği parçalar (açılan liste, takvim, kayan çubuk) onu okuyor — `index.css`'in başındaki not
 * bunun ölçülmüş hâli.
 */
export function applyTheme(theme: Theme): ResolvedTheme {
  const resolved = resolveTheme(theme)

  document.documentElement.classList.toggle(DARK_CLASS, resolved === 'dark')

  return resolved
}
